import type { Table } from '@tanstack/react-table';
import { dataTableStyles } from '@/shared/ui/data-table/styles';
import type { ResolvedDataTableProps, DataTableRow } from '@/shared/ui/data-table/types';
import { CardGrid } from './card-grid';
import { TableView } from './table-view';

type TableBodyProps<TData extends DataTableRow> = {
  presentation: 'table' | 'cards';
  /** Everything the body renders from, after defaults. */
  resolved: ResolvedDataTableProps<TData>;
  table: Table<TData>;
  /** Attach to the scrolling viewport so the height hook can measure it. */
  viewportRef?: (node: HTMLElement | null) => void;
  viewportStyle?: { maxHeight: string };
  /** Rendered only when infinite scrolling replaces the page controls. */
  showSentinel: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
};

/**
 * The results themselves: the failure message, the page of rows in whichever
 * presentation was asked for, and the infinite-scroll sentinel.
 *
 * An error renders as an alert ABOVE the table rather than replacing it. The
 * rows already on screen came from the server and are still true; replacing
 * them with an error would throw away the page the user had reached.
 */
export function TableBody<TData extends DataTableRow>({
  presentation,
  resolved,
  table,
  viewportRef,
  viewportStyle,
  showSentinel,
  sentinelRef,
}: TableBodyProps<TData>) {
  const { columns, data, rowKey, status, errorMessage, caption, tableCaption, selectable, sortControls } =
    resolved;

  // The empty row's copy. When the caller supplied no-match wording it is used for
  // a query that matched nothing; the collection-empty wording is the fallback, so
  // a caller that never distinguishes the two keeps the behaviour it had.
  const emptyTitle = resolved.noMatchTitle ?? resolved.emptyTitle;
  const emptyDescription = resolved.noMatchDescription ?? resolved.emptyDescription;

  return (
    <>
      {status === 'error' ? (
        <p className={dataTableStyles.alert} role="alert">
          {errorMessage}
        </p>
      ) : null}

      {presentation === 'cards' ? (
        <CardGrid
          columns={columns}
          data={data}
          rowKey={rowKey}
          status={status}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
        />
      ) : (
        <TableView
          table={table}
          caption={caption}
          tableCaption={tableCaption}
          selectable={selectable}
          sortControls={sortControls}
          status={status}
          viewportRef={viewportRef}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          viewportStyle={viewportStyle}
        />
      )}

      {/*
       * The sentinel is rendered in BOTH presentations. In table view it is
       * only reached when the caller opted out of paging; in card view it is
       * the primary way the next page arrives, because the grid has no footer
       * to page with.
       */}
      {showSentinel ? <div ref={sentinelRef} className={dataTableStyles.sentinel} /> : null}
    </>
  );
}